/**
 * API session client (login/user/logout + generic /api fetch).
 * Delegates CSRF/credentials/error handling to the central HTTP layer.
 */

import {
  sessionFetch,
  HttpError,
  classifyStatus,
  refreshCsrfSingleFlight,
  readXsrfToken,
  type ErrorKind,
} from './http';

const API_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

export { HttpError };
export type { ErrorKind };

/**
 * Back-compat error type used by login and older call sites.
 * Prefer HttpError for new code.
 */
export class ApiError extends HttpError {
  constructor(status: number, message: string, errors: Record<string, string[]> = {}) {
    const classified = classifyStatus(status);
    super({
      message,
      status,
      kind: classified.kind as ErrorKind,
      errors,
      requestId: 'legacy',
      retryable: classified.retryable,
    });
    this.name = 'ApiError';
  }
}

export async function parseApiError(response: Response): Promise<HttpError> {
  let message = response.statusText || 'Request failed';
  let errors: Record<string, string[]> = {};
  try {
    const body = await response.json();
    if (typeof body.message === 'string') message = body.message;
    if (body.errors && typeof body.errors === 'object') {
      for (const [k, v] of Object.entries(body.errors)) {
        errors[k] = Array.isArray(v) ? v.map(String) : [String(v)];
      }
    }
  } catch {
    /* non-JSON error body */
  }
  const status = response.status;
  const kind =
    status === 401 ? 'auth'
    : status === 403 ? 'forbidden'
    : status === 419 ? 'csrf'
    : status === 422 ? 'validation'
    : status === 404 ? 'not_found'
    : status === 429 ? 'rate_limit'
    : status >= 500 ? 'server'
    : 'unknown';
  return new HttpError({
    message,
    status,
    kind,
    errors,
    requestId: response.headers.get('X-Request-Id') || 'unknown',
    retryable: status === 419 || status === 429 || status >= 500,
  });
}

export async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const rawBody = options.body;
  let body: unknown = undefined;
  if (rawBody instanceof FormData) {
    body = rawBody;
  } else if (typeof rawBody === 'string') {
    try {
      body = JSON.parse(rawBody);
    } catch {
      body = rawBody;
    }
  } else if (rawBody != null) {
    body = rawBody;
  }

  return sessionFetch<T>({
    url: `${API_URL}${endpoint}`,
    method: options.method ?? 'GET',
    headers: options.headers,
    body,
    signal: options.signal,
  });
}

/** Ensure XSRF cookie exists (Laravel web session). Single-flight safe. */
export async function ensureCsrf(): Promise<{ message?: string; token?: string }> {
  await refreshCsrfSingleFlight();
  const token = readXsrfToken();
  return token ? { message: 'CSRF cookie set', token } : { message: 'CSRF cookie set' };
}
